/* eslint-disable @typescript-eslint/ban-types, @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef } from 'react';
import {
    IsArray,
    ArrayNotEmpty,
    IsNotEmpty,
    IsString,
    Length,
    Matches,
} from 'class-validator';
import { consumerUsernameRegex } from 'src/domain/consumer';
import {
    UserRole,
    checkIfUsernameExist,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import container from 'src/container';
import Form from 'src/components/FormComponents/Form';
import Input from 'src/components/FormComponents/Input';
import { useCurrentConsumerContext } from '../Home/NewHome/reducer';
import { useNotification } from 'src/components/Notification/useNotfication';
import AvatarInput, {
    HandleUploadAvatar,
} from 'src/components/FormComponents/AvatarInput';
import { mutate } from 'swr';
import { useLicenceInfo } from 'src/view/hooks/organization';
import { LinkButton } from 'src/components/Buttons/LinkButton';
import { SUPPORT_EMAIL } from 'src/config';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { LeftGroupSelect } from 'src/components/FormComponents/LeftGroupSelect';
import { OrganizationRolePermission } from 'src/domain/organization';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import { useConsumers } from 'src/view/hooks';
import {
    getAvailableLeafGroups,
    getOccupiedRoomIds,
} from 'src/utils/roomAvailability';

const lettersAndSpacesRegex = /^[\p{L} -]+$/u;

class ConsumerSchema {
    @IsString()
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @Matches(lettersAndSpacesRegex, {
        message: () => i18n.t('validation.lettersAndSpacesOnly'),
    })
    firstName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @Matches(lettersAndSpacesRegex, {
        message: () => i18n.t('validation.lettersAndSpacesOnly'),
    })
    lastName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @Length(3, 113, {
        message: () => i18n.t('validation.lengthBetween', { min: 3, max: 113 }),
    })
    @Matches(consumerUsernameRegex, {
        message: () => i18n.t('validation.usernameAllowedChars'),
    })
    username: string;

    @IsArray()
    @IsString({ each: true })
    @ArrayNotEmpty({ message: () => i18n.t('validation.requiredField') })
    groupIds: string[];
}
const {
    cradle: { munduClient },
} = container;

export const NewConsumer = () => {
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const avatarInputRef = useRef<HandleUploadAvatar>(null);
    const notify = useNotification();
    const { licenceLimitReached, isLoading: isLicenceLoading } =
        useLicenceInfo();
    const { t } = useTranslation();
    const history = useHistory();
    const { isCreator, activeRoleGroupIds } = useCreatorRoleContext();
    const canWriteResidents = useCreatorRolePermission(
        OrganizationRolePermission.writeResidents,
    );

    const { dispatch } = useCurrentConsumerContext();

    useEffect(() => {
        dispatch({ type: 'RESET' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        organizationId,
        organization,
        isLoading: isOrganizationLoading,
    } = useCurrentOrganization();
    const { consumers } = useConsumers({
        organizationId,
        enabled: Boolean(organizationId),
        applyCreatorFilter: isCreator,
    });

    const roleGroupAccess = useMemo(() => {
        if (
            !isCreator ||
            !canWriteResidents ||
            !activeRoleGroupIds.length ||
            !organization?.groups?.length
        ) {
            return {
                allowedGroupIds: undefined as string[] | undefined,
            };
        }

        const groups = organization.groups ?? [];
        const groupById = new Map(groups.map(group => [group.id, group]));
        const childrenByParent = new Map<string | undefined, string[]>();

        for (const group of groups) {
            const current = childrenByParent.get(group.parentId) ?? [];
            current.push(group.id);
            childrenByParent.set(group.parentId, current);
        }

        const allowedSet = new Set<string>();
        const addAncestors = (groupId: string) => {
            let currentId = groupById.get(groupId)?.parentId;
            while (currentId) {
                if (!allowedSet.has(currentId)) {
                    allowedSet.add(currentId);
                }
                currentId = groupById.get(currentId)?.parentId;
            }
        };

        const stack = [...activeRoleGroupIds];
        while (stack.length) {
            const currentId = stack.pop();
            if (!currentId || allowedSet.has(currentId)) continue;
            allowedSet.add(currentId);
            addAncestors(currentId);

            const children = childrenByParent.get(currentId) ?? [];
            children.forEach(childId => stack.push(childId));
        }

        for (const roleGroupId of activeRoleGroupIds) {
            let currentId = roleGroupId;
            let currentGroup = groupById.get(currentId);
            while (currentGroup?.parentId) {
                currentId = currentGroup.parentId;
                currentGroup = groupById.get(currentId);
            }

            if (currentGroup) {
                allowedSet.add(currentId);
            }
        }

        return {
            allowedGroupIds: [...allowedSet],
        };
    }, [
        activeRoleGroupIds,
        canWriteResidents,
        isCreator,
        organization?.groups,
    ]);
    const occupiedRoomIds = useMemo(
        () => getOccupiedRoomIds(consumers),
        [consumers],
    );
    const availableLeafGroups = useMemo(
        () =>
            getAvailableLeafGroups({
                organizationGroups: organization?.groups ?? [],
                allowedGroupIds: roleGroupAccess.allowedGroupIds,
                excludedGroupIds: occupiedRoomIds,
            }),
        [
            occupiedRoomIds,
            organization?.groups,
            roleGroupAccess.allowedGroupIds,
        ],
    );
    const hasAvailableRooms = availableLeafGroups.length > 0;

    if (!user || isLicenceLoading || isOrganizationLoading || !organizationId)
        return null;

    const resolvedOrganizationId = organizationId;
    const organizationNameForUsername = organization?.organizationName?.trim();

    if (licenceLimitReached) {
        return (
            <div className="flex flex-col max-w-3xl mx-auto">
                <div className="flex flex-col items-center gap-8">
                    <div className="text-3xl">{t('users.limit.title')}</div>
                    <div className="text-lg">
                        {t('users.limit.description')}
                    </div>
                    <LinkButton
                        title={t('users.limit.contact')}
                        fill="peachOrange"
                        textColor="woodyBrown"
                        to={`mailto:${SUPPORT_EMAIL}`}
                    />
                </div>
            </div>
        );
    }

    const onSubmit = async (data: ConsumerSchema) => {
        let followers = undefined;
        if (role === UserRole.creator && !isOrganizationAdministrator(user)) {
            followers = { creatorIds: [user.id] };
        }

        try {
            await checkIfUsernameExist(data.username);
            const newConsumer = await munduClient.createConsumer({
                profile: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                },
                username: data.username,
                organizationId: resolvedOrganizationId,
                followers,
                groupIds: data.groupIds,
            });
            await avatarInputRef.current?.uploadAvatar(newConsumer);
            notify(t('users.notifications.created'));
            mutate('consumers');
            history.push(`/app/users/profile/${newConsumer.id}`);
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <div className="flex flex-col max-w-3xl mx-auto">
            <Form schema={ConsumerSchema} onSubmit={onSubmit} mode="onChange">
                {({
                    register,
                    control,
                    clearErrors,
                    setValue,
                    watch,
                    formState: { errors, isSubmitting },
                }) => (
                    <div className="flex flex-col gap-4">
                        <UsernameAutoFill
                            organizationName={organizationNameForUsername}
                            watch={watch}
                            setValue={setValue}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-full">
                                <AvatarInput ref={avatarInputRef} />
                            </div>
                            <Input
                                className="text-input"
                                label={t('users.form.firstName')}
                                error={errors.firstName}
                                type="text"
                                {...register('firstName')}
                            />
                            <Input
                                className="text-input"
                                label={t('users.form.lastName')}
                                error={errors.lastName}
                                type="text"
                                {...register('lastName')}
                            />
                            <Input
                                containerClassName="col-span-full"
                                className="text-input"
                                error={errors.username}
                                label={t('users.form.username')}
                                type="text"
                                autoComplete="off"
                                {...register('username')}
                                disabled
                            />
                        </div>
                        <LeftGroupSelect
                            control={control}
                            name="groupIds"
                            organizationId={resolvedOrganizationId}
                            error={errors.groupIds as FieldError | undefined}
                            clearErrors={clearErrors}
                            allowedGroupIds={roleGroupAccess.allowedGroupIds}
                            excludedGroupIds={occupiedRoomIds}
                            emptyStateVariant="available"
                        />
                        <Input
                            value={t('users.form.submit')}
                            className="primary-btn-small"
                            disabled={isSubmitting || !hasAvailableRooms}
                            type="submit"
                        />
                    </div>
                )}
            </Form>
        </div>
    );
};

type UsernameAutoFillProps = {
    organizationName?: string;
    watch: UseFormWatch<ConsumerSchema>;
    setValue: UseFormSetValue<ConsumerSchema>;
};

const normalizeNameSegment = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'o')
        .replace(/å/g, 'a')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '_')
        .replace(/-/g, '_')
        .replace(/[^a-z0-9_]/g, '');

const UsernameAutoFill = ({
    organizationName,
    watch,
    setValue,
}: UsernameAutoFillProps) => {
    const [firstName, lastName, currentUsername] = watch([
        'firstName',
        'lastName',
        'username',
    ]);

    useEffect(() => {
        if (!organizationName) return;

        const nextUsername = [
            organizationName,
            firstName?.trim() ? normalizeNameSegment(firstName) : undefined,
            lastName?.trim() ? normalizeNameSegment(lastName) : undefined,
        ]
            .filter(Boolean)
            .join('_');

        if (nextUsername !== currentUsername) {
            setValue('username', nextUsername, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [organizationName, firstName, lastName, currentUsername, setValue]);

    return null;
};
